import { create } from "zustand";
import { INotification, api } from "../api/api";

interface NotificationsState {
  notifications: INotification[];
  unreadNotifications: INotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  isModalOpen: boolean;
  activeTab: 'all' | 'unread';
  selectedNotification: INotification | null;
  isLoadingDetails: boolean;

  // Действия
  fetchAllNotifications: () => Promise<void>;
  fetchUnreadNotifications: () => Promise<void>;
  getNotificationDetails: (id: number) => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  openModal: () => void;
  closeModal: () => void;
  clearError: () => void;
  setActiveTab: (tab: 'all' | 'unread') => void;
  selectNotification: (notification: INotification | null) => void;
}

const initialState = {
  notifications: [],
  unreadNotifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  isModalOpen: false,
  activeTab: 'all' as 'all' | 'unread',
  selectedNotification: null,
  isLoadingDetails: false,
};

export const useNotificationsStore = create<NotificationsState>()((set, get) => ({
  ...initialState,

  fetchAllNotifications: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.getAllNotifications();
      
      // Подсчитываем непрочитанные из общего списка
      const unreadFromAll = response.notifications?.filter(n => !n.is_checked) || [];
      
      set({ 
        notifications: response.notifications || [],
        unreadNotifications: unreadFromAll,
        unreadCount: unreadFromAll.length,
        isLoading: false 
      });
    } catch (error: any) {
      set({ 
        error: error.message || 'Ошибка при загрузке уведомлений',
        isLoading: false 
      });
    }
  },

  fetchUnreadNotifications: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.getUnreadNotifications();
      
      set({ 
        unreadNotifications: response.notifications || [],
        unreadCount: response.notifications?.length || 0,
        isLoading: false 
      });
    } catch (error: any) {
      set({ 
        error: error.message || 'Ошибка при загрузке непрочитанных уведомлений',
        isLoading: false 
      });
    }
  },

  getNotificationDetails: async (id: number): Promise<void> => {
    try {
      set({ isLoadingDetails: true, error: null });
      
      // 1. Получаем детали уведомления (GET /notifications/:id)
      const notification = await api.getNotificationById(id);
      
      // 2. Отображаем детали пользователю
      set({ 
        selectedNotification: notification,
        isLoadingDetails: false 
      });
      
      // 3. Если уведомление непрочитанное, помечаем его как прочитанное (POST /notifications/:id)
      if (!notification.is_checked) {
        await api.markNotificationAsRead(id);
        
        const { notifications, unreadNotifications } = get();
        
        // Обновляем основной список уведомлений
        const updatedNotifications = notifications.map(n =>
          n.id === id ? { ...n, is_checked: true } : n
        );
        
        // Убираем из непрочитанных
        const updatedUnreadNotifications = unreadNotifications.filter(
          n => n.id !== id
        );
        
        // Обновляем состояние с пометкой как прочитанное
        set({
          notifications: updatedNotifications,
          unreadNotifications: updatedUnreadNotifications,
          unreadCount: updatedUnreadNotifications.length,
          selectedNotification: { ...notification, is_checked: true }
        });
      }
    } catch (error: any) {
      set({ 
        error: error.message || 'Ошибка при загрузке деталей уведомления',
        isLoadingDetails: false 
      });
    }
  },

  markAsRead: async (id: number) => {
    try {
      await api.markNotificationAsRead(id);
      
      const { notifications, unreadNotifications } = get();
      
      // Обновляем основной список уведомлений
      const updatedNotifications = notifications.map(notification =>
        notification.id === id 
          ? { ...notification, is_checked: true }
          : notification
      );
      
      // Убираем из непрочитанных
      const updatedUnreadNotifications = unreadNotifications.filter(
        notification => notification.id !== id
      );
      
      set({
        notifications: updatedNotifications,
        unreadNotifications: updatedUnreadNotifications,
        unreadCount: updatedUnreadNotifications.length
      });
    } catch (error: any) {
      set({ 
        error: error.message || 'Ошибка при пометке уведомления как прочитанное'
      });
    }
  },

  openModal: () => {
    const { fetchAllNotifications, fetchUnreadNotifications } = get();
    set({ isModalOpen: true });
    // Загружаем данные для обеих вкладок при открытии модального окна
    fetchAllNotifications();
    fetchUnreadNotifications();
  },

  closeModal: () => {
    set({ isModalOpen: false, selectedNotification: null });
  },

  clearError: () => {
    set({ error: null });
  },

  setActiveTab: (tab: 'all' | 'unread') => {
    set({ activeTab: tab, selectedNotification: null });
  },

  selectNotification: (notification: INotification | null) => {
    set({ selectedNotification: notification });
  },
}));
